// bluaIQ — AI Insights & Learning Engine
// Tracks supplier usage, material patterns, budget trends, timeline performance
// Learns from each client's data and suggests improvements
// BUILDER plan exclusive ($209/mo)

import { apiFetch } from '../utils/api'
import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Brain, TrendingUp, TrendingDown, AlertTriangle, Lightbulb,
  Users, DollarSign, Clock, Package, Star, ChevronRight,
  BarChart3, ArrowUpRight, ArrowDownRight, Zap, Target,
  Download, RefreshCw, Lock
} from 'lucide-react'
import { downloadJson } from '../utils/exportJson'
import { isDemoProject } from '../utils/demoData'

// ── TYPES ─────────────────────────────────────────

interface SupplierInsight {
  name: string
  category: string
  timesUsed: number
  avgCost: number
  marketAvg: number
  savingsOrLoss: number // positive = saving, negative = overpaying
  avgDeliveryDays: number
  reliabilityScore: number // 1-5
  trend: 'up' | 'down' | 'stable'
  suggestion?: string
  suggestionEs?: string
}

interface MaterialInsight {
  material: string
  materialEs: string
  avgQtyPerProject: number
  unit: string
  avgCostPerUnit: number
  marketAvg: number
  trend: 'up' | 'down' | 'stable'
  lastPrice: number
  priceChange: number // percentage
  bestObservedPrice?: number
  avgLeadTimeDays?: number | null
}

interface BudgetInsight {
  category: string
  categoryEs: string
  avgBudgeted: number
  avgActual: number
  variance: number // percentage over/under
  trend: 'improving' | 'worsening' | 'stable'
  projects: number
}

interface TimelineInsight {
  phase: string
  phaseEs: string
  avgPlannedDays: number
  avgActualDays: number
  variance: number // days over/under
  bottleneck: boolean
}

interface AISuggestion {
  id: string
  type: 'savings' | 'efficiency' | 'risk' | 'quality'
  priority: 'high' | 'medium' | 'low'
  title: string
  titleEs: string
  description: string
  descriptionEs: string
  potentialSavings?: number
  confidence: number // 0-100
  source: string // what data this insight came from
  sourceEs?: string
}

interface ProjectIntelligenceSummary {
  capturedEvents: number
  takeoffsCompleted: number
  quotesReceived: number
  pricesRecorded: number
  selectionsChanged: number
  inspections: {
    requested: number
    scheduled: number
    passed: number
    failed: number
    retried: number
  }
  shadow: {
    comparedTakeoffs: number
    avgHvacSystemDelta: number | null
    avgHvacDuctDelta: number | null
    avgLightingFixtureDelta: number | null
    avgCabinetUnitDelta: number | null
    avgCountertopSfDelta: number | null
    avgFlooringSfDelta: number | null
    avgPaintAreaDelta: number | null
    avgApplianceUnitDelta: number | null
    avgPlumbingFixtureDelta: number | null
    avgPlumbingSupplyDelta: number | null
    avgElectricalWireDelta: number | null
    avgInsulationWallDelta: number | null
    avgDrywallRegularDelta: number | null
    avgDrywallTypeXDelta: number | null
    avgDrywallCornerBeadDelta: number | null
    avgDoorUnitDelta: number | null
    avgWindowUnitDelta: number | null
    latestComparedAt: string | null
  }
  recentSignals: Array<{
    eventName: string
    createdAt: string
  }>
}

interface ProjectTimelineTask {
  id: string
  name: string
  nameEs: string
  phase: string
  phaseEs: string
  status?: string
  startDate: string
  endDate: string
  progress: number
  dependencies: string[]
  color: string
  assignee: string
  isCritical?: boolean
  source?: string
  authority?: string
  externalKey?: string
  blocking?: boolean
  requiredUploads?: string[]
}

interface RealTimelinePhase {
  phase: string
  phaseEs: string
  totalTasks: number
  completedTasks: number
  blockedTasks: number
  inProgressTasks: number
  upcomingTasks: number
  earliestStart: string
  latestEnd: string
  criticalTasks: number
}

interface ProjectBudgetLineItem {
  id: string
  category: string
  label: string
  totalCost: number
}

interface ProjectBudget {
  id: string
  version: number
  totalCost: number
  createdAt: string
  lineItems: ProjectBudgetLineItem[]
}

interface ProjectReceipt {
  id: string
  total?: number | null
  items?: Array<{
    total?: number
    totalPrice?: number
    category?: string
  }> | null
}

interface RealBudgetCategory {
  category: string
  categoryEs: string
  budgeted: number
  actual: number
  variance: number
  itemCount: number
}

interface ProjectQuoteItem {
  id: string
  unitPrice: number
  quoteDate: string
  validUntil?: string
  paymentTerms?: string
  deliveryTerms?: string
  leadTimeDays?: number
  supplier?: { name: string }
  material?: { name: string; unit: string; category: string }
}

interface ProjectContact {
  id: string
  name: string
  company?: string | null
  role: string
  trade?: string | null
  rating?: number | null
}

interface AccountingPreview {
  provider: 'quickbooks' | 'xero' | 'sage_intacct'
  budgetTotal: number
  budgetLineCount: number
  approvedChangeOrderTotal: number
  latestDrawNumber: number | null
  latestDrawAmount: number
  pendingPaymentTotal: number
  paidPaymentTotal: number
  sampleBudgetLines: Array<{ category: string; label: string; amount: number }>
}

interface RampPreview {
  provider: 'ramp'
  receiptCount: number
  receiptSpendTotal: number
  topMerchants: Array<{ merchant: string; total: number }>
  topCategories: Array<{ category: string; total: number }>
  quoteSignals: Array<{ category: string; avgUnitPrice: number; quoteCount: number }>
}

interface IntegrationPreviewPayload {
  quickbooks: AccountingPreview
  xero: AccountingPreview
  sageIntacct: AccountingPreview
  ramp: RampPreview
}

function formatSignalLabel(eventName: string, isEs: boolean) {
  const labels: Record<string, { en: string; es: string }> = {
    takeoff_completed: { en: 'Takeoff completed', es: 'Takeoff completado' },
    takeoff_shadow_compared: { en: 'Shadow comparison saved', es: 'Comparación shadow guardada' },
    quote_received: { en: 'Quote received', es: 'Cotización recibida' },
    unit_price_recorded: { en: 'Unit price recorded', es: 'Precio unitario guardado' },
    selection_changed: { en: 'Selection updated', es: 'Selección actualizada' },
    integration_connected: { en: 'Integration connected', es: 'Integración conectada' },
    integration_updated: { en: 'Integration updated', es: 'Integración actualizada' },
    integration_disconnected: { en: 'Integration disconnected', es: 'Integración desconectada' },
    integration_tested: { en: 'Integration tested', es: 'Integración probada' },
    integration_exported: { en: 'Integration export generated', es: 'Exportación de integración generada' },
    inspection_requested: { en: 'Inspection requested', es: 'Inspección solicitada' },
    inspection_scheduled: { en: 'Inspection scheduled', es: 'Inspección programada' },
    inspection_failed: { en: 'Inspection failed', es: 'Inspección fallida' },
    inspection_retry: { en: 'Inspection retry', es: 'Reintento de inspección' },
    inspection_rescheduled: { en: 'Inspection rescheduled', es: 'Inspección reprogramada' },
    inspection_passed: { en: 'Inspection passed', es: 'Inspección aprobada' },
    payment_logged: { en: 'Payment logged', es: 'Pago registrado' },
    contract_created: { en: 'Contract created', es: 'Contrato creado' },
    draw_request_submitted: { en: 'Draw request submitted', es: 'Solicitud de cobro enviada' },
  }

  return isEs ? (labels[eventName]?.es || eventName) : (labels[eventName]?.en || eventName)
}

function deriveRealTimeline(tasks: ProjectTimelineTask[]): {
  phases: RealTimelinePhase[]
  totals: {
    totalTasks: number
    completedTasks: number
    blockedTasks: number
    inProgressTasks: number
    pendingTasks: number
  }
} {
  const byPhase = new Map<string, RealTimelinePhase>()

  for (const task of tasks) {
    const key = `${task.phase}__${task.phaseEs}`
    const existing = byPhase.get(key) || {
      phase: task.phase,
      phaseEs: task.phaseEs,
      totalTasks: 0,
      completedTasks: 0,
      blockedTasks: 0,
      inProgressTasks: 0,
      upcomingTasks: 0,
      earliestStart: task.startDate,
      latestEnd: task.endDate,
      criticalTasks: 0,
    }

    existing.totalTasks += 1
    if (task.status === 'COMPLETED') existing.completedTasks += 1
    else if (task.status === 'BLOCKED') existing.blockedTasks += 1
    else if (task.status === 'IN_PROGRESS') existing.inProgressTasks += 1
    else existing.upcomingTasks += 1

    if (task.isCritical) existing.criticalTasks += 1
    if (new Date(task.startDate).getTime() < new Date(existing.earliestStart).getTime()) existing.earliestStart = task.startDate
    if (new Date(task.endDate).getTime() > new Date(existing.latestEnd).getTime()) existing.latestEnd = task.endDate

    byPhase.set(key, existing)
  }

  const phases = Array.from(byPhase.values()).sort(
    (a, b) => new Date(a.earliestStart).getTime() - new Date(b.earliestStart).getTime()
  )

  return {
    phases,
    totals: {
      totalTasks: tasks.length,
      completedTasks: tasks.filter(task => task.status === 'COMPLETED').length,
      blockedTasks: tasks.filter(task => task.status === 'BLOCKED').length,
      inProgressTasks: tasks.filter(task => task.status === 'IN_PROGRESS').length,
      pendingTasks: tasks.filter(task => !task.status || task.status === 'PENDING').length,
    },
  }
}

function deriveRealBudget(
  budget: ProjectBudget | null | undefined,
  receipts: ProjectReceipt[] | null | undefined,
  fallbackLabels: BudgetInsight[]
): {
  categories: RealBudgetCategory[]
  totals: {
    budgeted: number
    actual: number
    variance: number
  }
} | null {
  if (!budget?.lineItems?.length) return null

  const categoryLabels = new Map(
    fallbackLabels.map(item => [item.category, item.categoryEs])
  )

  const spentByCategory = new Map<string, number>()
  for (const receipt of receipts || []) {
    for (const item of receipt.items || []) {
      const category = item.category || 'OTHER'
      const amount = typeof item.total === 'number'
        ? item.total
        : typeof item.totalPrice === 'number'
          ? item.totalPrice
          : 0
      spentByCategory.set(category, (spentByCategory.get(category) || 0) + amount)
    }
  }

  const grouped = new Map<string, RealBudgetCategory>()
  for (const lineItem of budget.lineItems) {
    const existing = grouped.get(lineItem.category) || {
      category: lineItem.category,
      categoryEs: categoryLabels.get(lineItem.category) || lineItem.category,
      budgeted: 0,
      actual: spentByCategory.get(lineItem.category) || 0,
      variance: 0,
      itemCount: 0,
    }
    existing.budgeted += lineItem.totalCost
    existing.itemCount += 1
    grouped.set(lineItem.category, existing)
  }

  const categories = Array.from(grouped.values())
    .map(item => ({
      ...item,
      variance: item.budgeted > 0
        ? Math.round((((item.actual - item.budgeted) / item.budgeted) * 100) * 10) / 10
        : 0,
    }))
    .sort((a, b) => b.budgeted - a.budgeted)

  const totalBudgeted = categories.reduce((sum, item) => sum + item.budgeted, 0)
  const totalActual = categories.reduce((sum, item) => sum + item.actual, 0)

  return {
    categories,
    totals: {
      budgeted: totalBudgeted,
      actual: totalActual,
      variance: totalBudgeted > 0
        ? Math.round((((totalActual - totalBudgeted) / totalBudgeted) * 100) * 10) / 10
        : 0,
    },
  }
}

function deriveRealMaterials(quotes: ProjectQuoteItem[] | null | undefined): MaterialInsight[] | null {
  if (!quotes?.length) return null

  const groups = new Map<string, {
    material: string
    unit: string
    category: string
    prices: number[]
    latestPrice: number
    latestDate: string
    avgLeadTimeDays: number[]
  }>()

  for (const quote of quotes) {
    const materialName = quote.material?.name
    if (!materialName || typeof quote.unitPrice !== 'number') continue

    const existing = groups.get(materialName) || {
      material: materialName,
      unit: quote.material?.unit || 'ea',
      category: quote.material?.category || 'OTHER',
      prices: [],
      latestPrice: quote.unitPrice,
      latestDate: quote.quoteDate,
      avgLeadTimeDays: [],
    }

    existing.prices.push(quote.unitPrice)
    if (quote.leadTimeDays && quote.leadTimeDays > 0) existing.avgLeadTimeDays.push(quote.leadTimeDays)
    if (new Date(quote.quoteDate).getTime() >= new Date(existing.latestDate).getTime()) {
      existing.latestPrice = quote.unitPrice
      existing.latestDate = quote.quoteDate
    }

    groups.set(materialName, existing)
  }

  const rows = Array.from(groups.values())
    .map(group => {
      const avgCostPerUnit = group.prices.reduce((sum, value) => sum + value, 0) / group.prices.length
      const marketAvg = avgCostPerUnit
      const minPrice = Math.min(...group.prices)
      const priceChange = avgCostPerUnit > 0
        ? Math.round((((group.latestPrice - avgCostPerUnit) / avgCostPerUnit) * 100) * 10) / 10
        : 0

      return {
        material: group.material,
        materialEs: group.material,
        avgQtyPerProject: group.prices.length,
        unit: group.unit,
        avgCostPerUnit: Math.round(avgCostPerUnit * 100) / 100,
        marketAvg: Math.round(marketAvg * 100) / 100,
        trend: priceChange > 2 ? 'up' as const : priceChange < -2 ? 'down' as const : 'stable' as const,
        lastPrice: group.latestPrice,
        priceChange,
        bestObservedPrice: minPrice,
        avgLeadTimeDays: group.avgLeadTimeDays.length > 0
          ? Math.round((group.avgLeadTimeDays.reduce((sum, value) => sum + value, 0) / group.avgLeadTimeDays.length) * 10) / 10
          : null,
      }
    })
    .sort((a, b) => b.avgQtyPerProject - a.avgQtyPerProject)

  return rows
}

function deriveRealSuppliers(
  quotes: ProjectQuoteItem[] | null | undefined,
  contacts: ProjectContact[] | null | undefined
): SupplierInsight[] | null {
  if (!quotes?.length) return null

  const contactRatings = new Map<string, number>()
  for (const contact of contacts || []) {
    const keys = [contact.name, contact.company].filter(Boolean) as string[]
    for (const key of keys) {
      if (typeof contact.rating === 'number') contactRatings.set(key.toLowerCase(), contact.rating)
    }
  }

  const groups = new Map<string, {
    name: string
    categories: string[]
    prices: number[]
    leadTimes: number[]
    quoteDates: string[]
  }>()

  for (const quote of quotes) {
    const supplierName = quote.supplier?.name
    if (!supplierName || typeof quote.unitPrice !== 'number') continue

    const existing = groups.get(supplierName) || {
      name: supplierName,
      categories: [],
      prices: [],
      leadTimes: [],
      quoteDates: [],
    }

    if (quote.material?.category) existing.categories.push(quote.material.category)
    existing.prices.push(quote.unitPrice)
    if (quote.leadTimeDays && quote.leadTimeDays > 0) existing.leadTimes.push(quote.leadTimeDays)
    existing.quoteDates.push(quote.quoteDate)
    groups.set(supplierName, existing)
  }

  return Array.from(groups.values())
    .map(group => {
      const avgCost = group.prices.reduce((sum, value) => sum + value, 0) / group.prices.length
      const marketAvg = avgCost
      const firstPrice = group.prices[0] || avgCost
      const lastPrice = group.prices[group.prices.length - 1] || avgCost
      const trendDelta = lastPrice - firstPrice
      const avgDeliveryDays = group.leadTimes.length > 0
        ? Math.round((group.leadTimes.reduce((sum, value) => sum + value, 0) / group.leadTimes.length) * 10) / 10
        : 0
      const rating = contactRatings.get(group.name.toLowerCase()) ?? 0

      return {
        name: group.name,
        category: group.categories[0] || 'OTHER',
        timesUsed: group.prices.length,
        avgCost: Math.round(avgCost * 100) / 100,
        marketAvg: Math.round(marketAvg * 100) / 100,
        savingsOrLoss: 0,
        avgDeliveryDays,
        reliabilityScore: rating,
        trend: trendDelta > 0.25 ? 'up' as const : trendDelta < -0.25 ? 'down' as const : 'stable' as const,
      }
    })
    .sort((a, b) => b.timesUsed - a.timesUsed)
}

function deriveRealSuggestions(
  intelligenceSummary: ProjectIntelligenceSummary | undefined,
  realBudget: { categories: RealBudgetCategory[]; totals: { budgeted: number; actual: number; variance: number } } | null,
  realTimeline: { phases: RealTimelinePhase[]; totals: { totalTasks: number; completedTasks: number; blockedTasks: number; inProgressTasks: number; pendingTasks: number } } | null,
  realMaterials: MaterialInsight[] | null,
  realSuppliers: SupplierInsight[] | null,
  integrationPreviews: IntegrationPreviewPayload | undefined,
  isEs: boolean
): AISuggestion[] | null {
  const suggestions: AISuggestion[] = []

  if (realBudget && realBudget.totals.variance > 5) {
    suggestions.push({
      id: 'real-budget-pressure',
      type: 'risk',
      priority: realBudget.totals.variance > 10 ? 'high' : 'medium',
      title: 'Budget pressure detected',
      titleEs: 'Presión de presupuesto detectada',
      description: `Actual tracked spending is ${realBudget.totals.variance.toFixed(1)}% over the current project budget. Review the heaviest categories before the next commitment.`,
      descriptionEs: `El gasto real rastreado va ${realBudget.totals.variance.toFixed(1)}% arriba del presupuesto actual del proyecto. Conviene revisar las categorías más pesadas antes del siguiente compromiso.`,
      confidence: 84,
      source: 'Project budget + receipts',
      sourceEs: 'Presupuesto del proyecto + receipts',
    })
  }

  if (realTimeline && realTimeline.totals.blockedTasks > 0) {
    suggestions.push({
      id: 'real-blocked-tasks',
      type: 'efficiency',
      priority: realTimeline.totals.blockedTasks > 2 ? 'high' : 'medium',
      title: 'Blocked tasks are slowing the schedule',
      titleEs: 'Las tareas bloqueadas están frenando el cronograma',
      description: `${realTimeline.totals.blockedTasks} lifecycle tasks are currently blocked. Clearing uploads, approvals, or inspection prerequisites will unlock schedule flow faster than adding more tasks.`,
      descriptionEs: `Hay ${realTimeline.totals.blockedTasks} tareas del lifecycle bloqueadas. Liberar uploads, aprobaciones o prerequisitos de inspección va a destrabar el cronograma más rápido que agregar más tareas.`,
      confidence: 88,
      source: 'Lifecycle + inspection workflow',
      sourceEs: 'Lifecycle + workflow de inspecciones',
    })
  }

  if (realMaterials && realMaterials.length > 0) {
    const slowest = [...realMaterials]
      .filter(item => typeof item.avgLeadTimeDays === 'number')
      .sort((a, b) => (b.avgLeadTimeDays || 0) - (a.avgLeadTimeDays || 0))[0]

    if (slowest && (slowest.avgLeadTimeDays || 0) >= 5) {
      suggestions.push({
        id: 'real-material-lead-time',
        type: 'risk',
        priority: 'medium',
        title: 'One material category needs earlier ordering',
        titleEs: 'Una categoría de material necesita ordenarse antes',
        description: `${slowest.material} is showing an average lead time of ${slowest.avgLeadTimeDays} days in project quotes. Pull that purchasing decision forward in the calendar.`,
        descriptionEs: `${slowest.material} está mostrando un lead time promedio de ${slowest.avgLeadTimeDays} días en las cotizaciones del proyecto. Conviene adelantar esa compra en el calendario.`,
        confidence: 79,
        source: 'Quote lead times',
        sourceEs: 'Lead times de cotizaciones',
      })
    }
  }

  if (realSuppliers && realSuppliers.length > 0) {
    const rated = realSuppliers.filter(item => item.reliabilityScore > 0)
    const weakest = rated.sort((a, b) => a.reliabilityScore - b.reliabilityScore)[0]
    if (weakest && weakest.reliabilityScore <= 3) {
      suggestions.push({
        id: 'real-supplier-rating',
        type: 'quality',
        priority: 'medium',
        title: 'A rated supplier may need review',
        titleEs: 'Un proveedor calificado podría necesitar revisión',
        description: `${weakest.name} is one of the lowest-rated supplier contacts on this project (${weakest.reliabilityScore}/5). Review whether to keep using them for upcoming buys.`,
        descriptionEs: `${weakest.name} es uno de los contactos/proveedores peor calificados en este proyecto (${weakest.reliabilityScore}/5). Conviene revisar si lo seguimos usando en próximas compras.`,
        confidence: 73,
        source: 'Project contacts + quotes',
        sourceEs: 'Contactos del proyecto + cotizaciones',
      })
    }
  }

  if (integrationPreviews?.ramp && integrationPreviews.ramp.receiptSpendTotal > 0 && integrationPreviews.ramp.topCategories[0]) {
    suggestions.push({
      id: 'real-spend-signal',
      type: 'savings',
      priority: 'low',
      title: 'Spend sync is ready for finance review',
      titleEs: 'El sync de gastos ya está listo para revisión financiera',
      description: `Captured receipts already show ${integrationPreviews.ramp.receiptSpendTotal.toLocaleString()} in tracked spend, led by ${integrationPreviews.ramp.topCategories[0].category}. This is enough to start a Ramp export package.`,
      descriptionEs: `Los receipts capturados ya muestran ${integrationPreviews.ramp.receiptSpendTotal.toLocaleString()} en gasto rastreado, liderado por ${integrationPreviews.ramp.topCategories[0].category}. Ya alcanza para generar un paquete de exportación hacia Ramp.`,
      confidence: 76,
      source: 'Receipts + spend preview',
      sourceEs: 'Receipts + preview de gasto',
    })
  }

  if (intelligenceSummary && intelligenceSummary.shadow.comparedTakeoffs > 0) {
    suggestions.push({
      id: 'real-shadow-learning',
      type: 'quality',
      priority: 'low',
      title: 'Shadow takeoff is already generating learning signal',
      titleEs: 'El takeoff shadow ya está generando señal de aprendizaje',
      description: `The project already has ${intelligenceSummary.shadow.comparedTakeoffs} shadow comparisons. Keep confirming takeoffs so NATAMI can calibrate future quantities with real project behavior.`,
      descriptionEs: `El proyecto ya tiene ${intelligenceSummary.shadow.comparedTakeoffs} comparaciones shadow. Conviene seguir confirmando takeoffs para que NATAMI calibre cantidades futuras con comportamiento real del proyecto.`,
      confidence: 81,
      source: 'Takeoff v2 shadow comparisons',
      sourceEs: 'Comparaciones de Takeoff v2 shadow',
    })
  }

  return suggestions.length > 0 ? suggestions : null
}

// ── DEMO DATA ─────────────────────────────────────

const DEMO_SUPPLIERS: SupplierInsight[] = [
  {
    name: '84 Lumber', category: 'Framing',
    timesUsed: 12, avgCost: 28500, marketAvg: 26800, savingsOrLoss: -1700,
    avgDeliveryDays: 5, reliabilityScore: 4.2, trend: 'stable',
    suggestion: 'You\'re paying ~6% above market for framing lumber. Consider getting a quote from BMC or Builders FirstSource for your next project.',
    suggestionEs: 'Estas pagando ~6% arriba del mercado en madera para estructura. Considera pedir cotizacion a BMC o Builders FirstSource para tu proximo proyecto.',
  },
  {
    name: 'Ferguson Plumbing', category: 'Plumbing',
    timesUsed: 8, avgCost: 12400, marketAvg: 13100, savingsOrLoss: 700,
    avgDeliveryDays: 3, reliabilityScore: 4.8, trend: 'stable',
    suggestion: 'Great supplier — 5% below market and reliable. Keep using them.',
    suggestionEs: 'Buen proveedor — 5% debajo del mercado y confiable. Sigue usandolos.',
  },
  {
    name: 'Carlos Electric LLC', category: 'Electrical',
    timesUsed: 10, avgCost: 8900, marketAvg: 8200, savingsOrLoss: -700,
    avgDeliveryDays: 8, reliabilityScore: 3.1, trend: 'down',
    suggestion: 'Carlos Electric has been late on 4 of last 6 projects (avg 3 days late). Their prices are 8.5% above market. Consider qualifying a second electrician.',
    suggestionEs: 'Carlos Electric ha llegado tarde en 4 de los ultimos 6 proyectos (promedio 3 dias tarde). Sus precios estan 8.5% arriba del mercado. Considera calificar un segundo electricista.',
  },
  {
    name: 'Precision Drywall', category: 'Drywall',
    timesUsed: 9, avgCost: 7200, marketAvg: 7500, savingsOrLoss: 300,
    avgDeliveryDays: 4, reliabilityScore: 4.5, trend: 'up',
    suggestion: 'Solid performance and competitive pricing. They\'ve improved delivery times over the last 3 projects.',
    suggestionEs: 'Buen desempeno y precios competitivos. Han mejorado tiempos de entrega en los ultimos 3 proyectos.',
  },
  {
    name: 'ABC Supply', category: 'Roofing',
    timesUsed: 7, avgCost: 9800, marketAvg: 9500, savingsOrLoss: -300,
    avgDeliveryDays: 6, reliabilityScore: 3.8, trend: 'stable',
  },
  {
    name: 'Floor & Decor', category: 'Flooring',
    timesUsed: 11, avgCost: 5600, marketAvg: 6200, savingsOrLoss: 600,
    avgDeliveryDays: 2, reliabilityScore: 4.6, trend: 'stable',
    suggestion: 'Saving 10% vs market on flooring materials. Your most cost-effective supplier.',
    suggestionEs: 'Ahorrando 10% vs mercado en pisos. Tu proveedor mas economico.',
  },
]

const DEMO_MATERIALS: MaterialInsight[] = [
  { material: '2x4x8 SPF Studs', materialEs: 'Polines 2x4x8', avgQtyPerProject: 420, unit: 'pcs', avgCostPerUnit: 3.85, marketAvg: 3.72, trend: 'up', lastPrice: 4.10, priceChange: 6.5 },
  { material: '7/16" OSB Sheathing', materialEs: 'OSB 7/16"', avgQtyPerProject: 65, unit: 'sheets', avgCostPerUnit: 18.50, marketAvg: 19.20, trend: 'down', lastPrice: 17.80, priceChange: -3.8 },
  { material: 'LVP Flooring', materialEs: 'Piso LVP', avgQtyPerProject: 1400, unit: 'sqft', avgCostPerUnit: 3.20, marketAvg: 3.50, trend: 'stable', lastPrice: 3.25, priceChange: 1.6 },
  { material: 'Interior Paint (5 gal)', materialEs: 'Pintura Interior (5 gal)', avgQtyPerProject: 12, unit: 'buckets', avgCostPerUnit: 165, marketAvg: 158, trend: 'up', lastPrice: 172, priceChange: 4.2 },
  { material: 'Shingles (Architectural)', materialEs: 'Tejas (Arquitectonicas)', avgQtyPerProject: 35, unit: 'bundles', avgCostPerUnit: 38.50, marketAvg: 37.80, trend: 'stable', lastPrice: 39.00, priceChange: 1.3 },
  { material: 'Concrete (Ready Mix)', materialEs: 'Concreto (Premezclado)', avgQtyPerProject: 18, unit: 'yards', avgCostPerUnit: 165, marketAvg: 160, trend: 'up', lastPrice: 170, priceChange: 3.0 },
]

const DEMO_BUDGET: BudgetInsight[] = [
  { category: 'Framing', categoryEs: 'Estructura', avgBudgeted: 32000, avgActual: 33500, variance: 4.7, trend: 'worsening', projects: 8 },
  { category: 'Flooring', categoryEs: 'Pisos', avgBudgeted: 8500, avgActual: 7800, variance: -8.2, trend: 'improving', projects: 8 },
  { category: 'Plumbing', categoryEs: 'Plomeria', avgBudgeted: 14000, avgActual: 13200, variance: -5.7, trend: 'stable', projects: 8 },
  { category: 'Electrical', categoryEs: 'Electrico', avgBudgeted: 9000, avgActual: 10100, variance: 12.2, trend: 'worsening', projects: 8 },
  { category: 'Roofing', categoryEs: 'Techo', avgBudgeted: 11000, avgActual: 10800, variance: -1.8, trend: 'stable', projects: 8 },
  { category: 'Drywall', categoryEs: 'Tablaroca', avgBudgeted: 7500, avgActual: 7300, variance: -2.7, trend: 'improving', projects: 8 },
  { category: 'Paint', categoryEs: 'Pintura', avgBudgeted: 4500, avgActual: 5200, variance: 15.6, trend: 'worsening', projects: 8 },
  { category: 'Finishes', categoryEs: 'Acabados', avgBudgeted: 18000, avgActual: 20500, variance: 13.9, trend: 'worsening', projects: 8 },
]

const DEMO_TIMELINE: TimelineInsight[] = [
  { phase: 'Foundation', phaseEs: 'Cimentacion', avgPlannedDays: 7, avgActualDays: 8, variance: 1, bottleneck: false },
  { phase: 'Framing', phaseEs: 'Estructura', avgPlannedDays: 14, avgActualDays: 16, variance: 2, bottleneck: false },
  { phase: 'Roofing', phaseEs: 'Techo', avgPlannedDays: 5, avgActualDays: 5, variance: 0, bottleneck: false },
  { phase: 'MEP Rough-in', phaseEs: 'Instalaciones base', avgPlannedDays: 10, avgActualDays: 14, variance: 4, bottleneck: true },
  { phase: 'Insulation + Drywall', phaseEs: 'Aislamiento + Tablaroca', avgPlannedDays: 8, avgActualDays: 9, variance: 1, bottleneck: false },
  { phase: 'Finishes', phaseEs: 'Acabados', avgPlannedDays: 21, avgActualDays: 28, variance: 7, bottleneck: true },
  { phase: 'Punch List + Close', phaseEs: 'Detalles + Cierre', avgPlannedDays: 7, avgActualDays: 12, variance: 5, bottleneck: true },
]

const DEMO_SUGGESTIONS: AISuggestion[] = [
  {
    id: 'ai-1', type: 'savings', priority: 'high',
    title: 'Switch framing supplier', titleEs: 'Cambiar proveedor de estructura',
    description: 'Based on 12 projects, you\'re paying $1,700 above market per house for framing materials at 84 Lumber. Getting quotes from BMC could save $20,400/year.',
    descriptionEs: 'Basado en 12 proyectos, estas pagando $1,700 arriba del mercado por casa en madera de estructura con 84 Lumber. Cotizar con BMC podria ahorrarte $20,400/ano.',
    potentialSavings: 20400, confidence: 87, source: '12 projects analyzed', sourceEs: '12 proyectos analizados',
  },
  {
    id: 'ai-2', type: 'risk', priority: 'high',
    title: 'Electrical sub reliability issue', titleEs: 'Problema de confiabilidad con electricista',
    description: 'Carlos Electric has been late on 67% of recent projects, averaging 3 days behind schedule. This delays drywall, painting, and final inspections. Consider qualifying backup electrical sub.',
    descriptionEs: 'Carlos Electric ha llegado tarde en 67% de proyectos recientes, con promedio de 3 dias de retraso. Esto retrasa tablaroca, pintura e inspecciones finales. Considera calificar un electricista de respaldo.',
    confidence: 92, source: '10 projects, 6 months', sourceEs: '10 proyectos, 6 meses',
  },
  {
    id: 'ai-3', type: 'efficiency', priority: 'medium',
    title: 'Finish selections causing delays', titleEs: 'Seleccion de acabados causando retrasos',
    description: 'Your finishes phase runs 33% over schedule on average (28 days vs 21 planned). Most delays come from late material selections. Using the Finish Selections Wizard earlier in the project could save 5-7 days.',
    descriptionEs: 'Tu fase de acabados tarda 33% mas de lo planeado en promedio (28 dias vs 21 planeados). La mayoria de retrasos vienen de selecciones tardias de materiales. Usar el Wizard de Acabados mas temprano podria ahorrarte 5-7 dias.',
    confidence: 78, source: '8 projects timeline analysis', sourceEs: '8 proyectos analizados',
  },
  {
    id: 'ai-4', type: 'savings', priority: 'medium',
    title: 'Budget consistently over on paint + finishes', titleEs: 'Presupuesto consistentemente excedido en pintura + acabados',
    description: 'Paint and finishes are over budget by 15.6% and 13.9% respectively across 8 projects. Recommend increasing these line items by 12% in future estimates to avoid surprises.',
    descriptionEs: 'Pintura y acabados se exceden del presupuesto por 15.6% y 13.9% respectivamente en 8 proyectos. Recomendamos incrementar estos rubros 12% en futuras estimaciones para evitar sorpresas.',
    potentialSavings: 3200, confidence: 85, source: '8 project budgets', sourceEs: '8 presupuestos de proyectos',
  },
  {
    id: 'ai-5', type: 'quality', priority: 'low',
    title: 'Floor & Decor is your best value supplier', titleEs: 'Floor & Decor es tu mejor proveedor en valor',
    description: 'Saving 10% vs market on flooring with great reliability (4.6/5). Consider expanding orders to include backsplash and bathroom tile.',
    descriptionEs: 'Ahorrando 10% vs mercado en pisos con buena confiabilidad (4.6/5). Considera expandir pedidos para incluir backsplash y ceramica de banos.',
    confidence: 90, source: '11 project purchases', sourceEs: '11 compras de proyectos',
  },
]

// ── COMPONENT ─────────────────────────────────────

export default function InsightsModule() {
  const { projectId } = useParams()
  const { getToken } = useAuth()
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const isEs = i18n.language === 'es'
  // PR-NEW: honest-empty-states gate. Demo projects (DEMO_PROJECT_IDS in
  // src/utils/demoData.ts) keep their preloaded DEMO_* insights so the
  // demo experience stays fully populated. Real projects with no
  // insight data must show an honest empty state instead of having
  // demo suggestions/suppliers/budget/timeline leak into their surface.
  const isDemo = isDemoProject(projectId)

  const [activeTab, setActiveTab] = useState<'suggestions' | 'suppliers' | 'materials' | 'budget' | 'timeline'>('suggestions')

  const { data: intelligenceSummary } = useQuery<ProjectIntelligenceSummary>({
    queryKey: ['project-intelligence-summary', projectId],
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/intelligence-summary`, {
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
      })
      if (!res.ok) throw new Error('Intelligence summary unavailable')
      return res.json()
    },
    retry: false,
    enabled: Boolean(projectId),
  })

  const { data: projectTimelineTasks } = useQuery<ProjectTimelineTask[]>({
    queryKey: ['project-timeline-tasks', projectId],
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/tasks`, {
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
      })
      if (!res.ok) throw new Error('Project timeline unavailable')
      return res.json()
    },
    retry: false,
    enabled: Boolean(projectId),
  })

  const { data: projectBudget } = useQuery<ProjectBudget | null>({
    queryKey: ['project-budget-insights', projectId],
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/budget`, {
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
      })
      if (!res.ok) throw new Error('Project budget unavailable')
      return res.json()
    },
    retry: false,
    enabled: Boolean(projectId),
  })

  const { data: projectReceipts } = useQuery<ProjectReceipt[]>({
    queryKey: ['project-receipts-insights', projectId],
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/receipts`, {
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
      })
      if (!res.ok) throw new Error('Project receipts unavailable')
      return res.json()
    },
    retry: false,
    enabled: Boolean(projectId),
  })

  const { data: projectQuotes } = useQuery<ProjectQuoteItem[]>({
    queryKey: ['project-quotes-insights', projectId],
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/quotes`, {
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
      })
      if (!res.ok) throw new Error('Project quotes unavailable')
      return res.json()
    },
    retry: false,
    enabled: Boolean(projectId),
  })

  const { data: projectContacts } = useQuery<ProjectContact[]>({
    queryKey: ['project-contacts-insights', projectId],
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/contacts`, {
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
      })
      if (!res.ok) throw new Error('Project contacts unavailable')
      return res.json()
    },
    retry: false,
    enabled: Boolean(projectId),
  })

  const { data: integrationPreviews } = useQuery<IntegrationPreviewPayload>({
    queryKey: ['integration-previews-insights', projectId],
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/integrations/previews`, {
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
      })
      if (!res.ok) throw new Error('Integration previews unavailable')
      return res.json()
    },
    retry: false,
    enabled: Boolean(projectId),
  })

  // Calculate totals
  const realTimeline = projectTimelineTasks && projectTimelineTasks.length > 0
    ? deriveRealTimeline(projectTimelineTasks)
    : null
  const realBudget = deriveRealBudget(projectBudget, projectReceipts, DEMO_BUDGET)
  const realMaterials = deriveRealMaterials(projectQuotes)
  const realSuppliers = deriveRealSuppliers(projectQuotes, projectContacts)
  const realSuggestions = deriveRealSuggestions(intelligenceSummary, realBudget, realTimeline, realMaterials, realSuppliers, integrationPreviews, isEs)

  // PR-NEW: every "real data missing → DEMO_*" fallback below is now
  // gated by isDemo. On a real project with no data, these summary
  // KPIs return 0 instead of demo-derived numbers — the honest
  // empty-state banner (rendered below the header) explains why.
  const totalSavingsOpportunity = useMemo(() => {
    const source = realSuggestions && realSuggestions.length > 0
      ? realSuggestions
      : (isDemo ? DEMO_SUGGESTIONS : [])
    return source.filter(s => s.potentialSavings).reduce((sum, s) => sum + (s.potentialSavings || 0), 0)
  }, [realSuggestions, isDemo])

  const avgReliability = useMemo(() => {
    const rated = (realSuppliers && realSuppliers.length > 0
      ? realSuppliers
      : (isDemo ? DEMO_SUPPLIERS : []))
      .filter(item => typeof item.reliabilityScore === 'number' && item.reliabilityScore > 0)
    if (rated.length === 0) return 0
    return rated.reduce((sum, item) => sum + item.reliabilityScore, 0) / rated.length
  }, [realSuppliers, isDemo])

  const overBudgetCategories = realBudget
    ? realBudget.categories.filter(category => category.variance > 5).length
    : (isDemo ? DEMO_BUDGET.filter(b => b.variance > 5).length : 0)

  const bottleneckPhases = realTimeline
    ? realTimeline.phases.filter(phase => phase.blockedTasks > 0 || phase.criticalTasks > 0).length
    : (isDemo ? DEMO_TIMELINE.filter(t => t.bottleneck).length : 0)

  // Has any real insight data of any kind? If false AND we are on a
  // real project, the honest empty state replaces the tab content.
  const hasAnyRealInsightData =
    (realSuggestions && realSuggestions.length > 0) ||
    (realSuppliers && realSuppliers.length > 0) ||
    (realMaterials && realMaterials.length > 0) ||
    (realBudget && realBudget.categories.length > 0) ||
    (realTimeline && realTimeline.phases.length > 0)

  const insightsSnapshot = {
    intelligenceSummary,
    realBudget,
    realTimeline,
    realMaterials,
    realSuppliers,
    realSuggestions,
    integrationPreviews,
    exportedAt: new Date().toISOString(),
  }

  const tabs = [
    // PR-NEW: tab count fallbacks gated by isDemo. On a real project
    // with no data, counts render 0 instead of inheriting DEMO_*.length.
    { key: 'suggestions', label: isEs ? 'Sugerencias AI' : 'AI Suggestions', icon: Lightbulb, count: realSuggestions?.length || (isDemo ? DEMO_SUGGESTIONS.length : 0) },
    { key: 'suppliers', label: isEs ? 'Proveedores' : 'Suppliers', icon: Users, count: realSuppliers?.length || (isDemo ? DEMO_SUPPLIERS.length : 0) },
    { key: 'materials', label: isEs ? 'Materiales' : 'Materials', icon: Package, count: realMaterials?.length || (isDemo ? DEMO_MATERIALS.length : 0) },
    { key: 'budget', label: isEs ? 'Presupuesto' : 'Budget', icon: DollarSign, count: realBudget?.categories.length || (isDemo ? DEMO_BUDGET.length : 0) },
    { key: 'timeline', label: isEs ? 'Tiempos' : 'Timeline', icon: Clock, count: realTimeline?.phases.length || (isDemo ? DEMO_TIMELINE.length : 0) },
  ]

  return (
    <div className="p-5 max-w-5xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17] flex items-center gap-2">
            <Brain size={18} className="text-[#06539B]" />
            {isEs ? 'NATAMI Insights' : 'NATAMI Insights'}
          </h2>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
            {isEs
              ? intelligenceSummary
                ? `IA aprendiendo de tu proyecto · ${intelligenceSummary.capturedEvents} señales operativas capturadas`
                : 'IA aprendiendo de tus proyectos'
              : intelligenceSummary
                ? `AI learning from this project · ${intelligenceSummary.capturedEvents} operating signals captured`
                : 'AI learning from your projects'
            }
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => downloadJson(`insights-${projectId || 'project'}-snapshot`, insightsSnapshot)}
            className="flex items-center gap-1.5 border border-[#ECEAE4] text-[13px] font-[800] px-3 py-2 rounded-full hover:bg-[#F2F0EB] transition-colors"
          >
            <Download size={12} /> {isEs ? 'Exportar' : 'Export'}
          </button>
          <button
            onClick={() => {
              void queryClient.invalidateQueries({ queryKey: ['project-intelligence-summary', projectId] })
              void queryClient.invalidateQueries({ queryKey: ['project-timeline-tasks', projectId] })
              void queryClient.invalidateQueries({ queryKey: ['project-budget-insights', projectId] })
              void queryClient.invalidateQueries({ queryKey: ['project-receipts-insights', projectId] })
              void queryClient.invalidateQueries({ queryKey: ['project-quotes-insights', projectId] })
              void queryClient.invalidateQueries({ queryKey: ['project-contacts-insights', projectId] })
              void queryClient.invalidateQueries({ queryKey: ['integration-previews-insights', projectId] })
            }}
            className="flex items-center gap-1.5 bg-[#06539B] text-white text-[13px] font-[800] px-4 py-2.5 rounded-full hover:bg-[#14457E] transition-colors"
          >
            <RefreshCw size={12} /> {isEs ? 'Actualizar datos' : 'Refresh data'}
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      {intelligenceSummary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-[#F8FBFF] border border-[#DBEAFE] rounded-xl p-4">
            <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#06539B]">
              {isEs ? 'Eventos capturados' : 'Captured events'}
            </div>
            <div className="text-[22px] font-[900] tracking-[-1px] text-[#1C1A17]">{intelligenceSummary.capturedEvents}</div>
            <div className="text-[9px] font-[600] text-[#9E9890]">{isEs ? 'base operativa real' : 'real operating base'}</div>
          </div>
          <div className="bg-[#F8FBFF] border border-[#DBEAFE] rounded-xl p-4">
            <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#06539B]">
              {isEs ? 'Takeoffs cerrados' : 'Completed takeoffs'}
            </div>
            <div className="text-[22px] font-[900] tracking-[-1px] text-[#1C1A17]">{intelligenceSummary.takeoffsCompleted}</div>
            <div className="text-[9px] font-[600] text-[#9E9890]">{isEs ? 'con señal guardada' : 'with stored signal'}</div>
          </div>
          <div className="bg-[#F8FBFF] border border-[#DBEAFE] rounded-xl p-4">
            <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#06539B]">
              {isEs ? 'Shadow comparado' : 'Shadow compared'}
            </div>
            <div className="text-[22px] font-[900] tracking-[-1px] text-[#1C1A17]">{intelligenceSummary.shadow.comparedTakeoffs}</div>
            <div className="text-[9px] font-[600] text-[#9E9890]">
              {intelligenceSummary.shadow.avgHvacSystemDelta !== null || intelligenceSummary.shadow.avgCabinetUnitDelta !== null || intelligenceSummary.shadow.avgCountertopSfDelta !== null || intelligenceSummary.shadow.avgFlooringSfDelta !== null || intelligenceSummary.shadow.avgPaintAreaDelta !== null || intelligenceSummary.shadow.avgApplianceUnitDelta !== null || intelligenceSummary.shadow.avgInsulationWallDelta !== null || intelligenceSummary.shadow.avgDrywallRegularDelta !== null || intelligenceSummary.shadow.avgDoorUnitDelta !== null || intelligenceSummary.shadow.avgWindowUnitDelta !== null
                ? `${intelligenceSummary.shadow.avgHvacSystemDelta !== null ? `${isEs ? 'hvac' : 'hvac'} ${intelligenceSummary.shadow.avgHvacSystemDelta >= 0 ? '+' : ''}${intelligenceSummary.shadow.avgHvacSystemDelta} · ` : ''}${intelligenceSummary.shadow.avgLightingFixtureDelta !== null ? `${isEs ? 'luz.' : 'lights'} ${intelligenceSummary.shadow.avgLightingFixtureDelta >= 0 ? '+' : ''}${intelligenceSummary.shadow.avgLightingFixtureDelta} · ` : ''}${intelligenceSummary.shadow.avgCabinetUnitDelta !== null ? `${isEs ? 'gabs.' : 'cabs.'} ${intelligenceSummary.shadow.avgCabinetUnitDelta >= 0 ? '+' : ''}${intelligenceSummary.shadow.avgCabinetUnitDelta} · ` : ''}${intelligenceSummary.shadow.avgCountertopSfDelta !== null ? `${isEs ? 'tops.' : 'tops.'} ${intelligenceSummary.shadow.avgCountertopSfDelta >= 0 ? '+' : ''}${intelligenceSummary.shadow.avgCountertopSfDelta} · ` : ''}${intelligenceSummary.shadow.avgFlooringSfDelta !== null ? `${isEs ? 'pisos' : 'floor'} ${intelligenceSummary.shadow.avgFlooringSfDelta >= 0 ? '+' : ''}${intelligenceSummary.shadow.avgFlooringSfDelta} · ` : ''}${intelligenceSummary.shadow.avgPaintAreaDelta !== null ? `${isEs ? 'pint.' : 'paint'} ${intelligenceSummary.shadow.avgPaintAreaDelta >= 0 ? '+' : ''}${intelligenceSummary.shadow.avgPaintAreaDelta} · ` : ''}${intelligenceSummary.shadow.avgApplianceUnitDelta !== null ? `${isEs ? 'appl.' : 'appl.'} ${intelligenceSummary.shadow.avgApplianceUnitDelta >= 0 ? '+' : ''}${intelligenceSummary.shadow.avgApplianceUnitDelta} · ` : ''}${intelligenceSummary.shadow.avgPlumbingFixtureDelta !== null ? `${isEs ? 'fx.' : 'fx.'} ${intelligenceSummary.shadow.avgPlumbingFixtureDelta >= 0 ? '+' : ''}${intelligenceSummary.shadow.avgPlumbingFixtureDelta} · ` : ''}${intelligenceSummary.shadow.avgPlumbingSupplyDelta !== null ? `${isEs ? 'plom.' : 'plumb.'} ${intelligenceSummary.shadow.avgPlumbingSupplyDelta >= 0 ? '+' : ''}${intelligenceSummary.shadow.avgPlumbingSupplyDelta} · ` : ''}${intelligenceSummary.shadow.avgElectricalWireDelta !== null ? `${isEs ? 'elec.' : 'elec.'} ${intelligenceSummary.shadow.avgElectricalWireDelta >= 0 ? '+' : ''}${intelligenceSummary.shadow.avgElectricalWireDelta} · ` : ''}${intelligenceSummary.shadow.avgInsulationWallDelta !== null ? `${isEs ? 'aisl.' : 'insul.'} ${intelligenceSummary.shadow.avgInsulationWallDelta >= 0 ? '+' : ''}${intelligenceSummary.shadow.avgInsulationWallDelta} · ` : ''}${intelligenceSummary.shadow.avgDrywallRegularDelta !== null ? `${isEs ? 'drywall prom.' : 'avg drywall'} ${intelligenceSummary.shadow.avgDrywallRegularDelta >= 0 ? '+' : ''}${intelligenceSummary.shadow.avgDrywallRegularDelta}` : `${isEs ? 'shadow activo' : 'shadow active'}`}${intelligenceSummary.shadow.avgDoorUnitDelta !== null ? ` · ${isEs ? 'puertas' : 'doors'} ${intelligenceSummary.shadow.avgDoorUnitDelta >= 0 ? '+' : ''}${intelligenceSummary.shadow.avgDoorUnitDelta}` : ''}${intelligenceSummary.shadow.avgWindowUnitDelta !== null ? ` · ${isEs ? 'ventanas' : 'windows'} ${intelligenceSummary.shadow.avgWindowUnitDelta >= 0 ? '+' : ''}${intelligenceSummary.shadow.avgWindowUnitDelta}` : ''}`
                : (isEs ? 'sin promedio todavía' : 'no average yet')}
            </div>
          </div>
          <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-xl p-4">
            <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#C2410C]">
              {isEs ? 'Inspecciones fallidas' : 'Failed inspections'}
            </div>
            <div className="text-[22px] font-[900] tracking-[-1px] text-[#7C2D12]">{intelligenceSummary.inspections.failed}</div>
            <div className="text-[9px] font-[600] text-[#9E9890]">
              {isEs
                ? `${intelligenceSummary.inspections.retried} reintentos registrados`
                : `${intelligenceSummary.inspections.retried} retries recorded`}
            </div>
          </div>
          <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl p-4">
            <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#15803D]">
              {isEs ? 'Compras capturadas' : 'Purchase signals'}
            </div>
            <div className="text-[22px] font-[900] tracking-[-1px] text-[#166534]">
              {intelligenceSummary.quotesReceived + intelligenceSummary.pricesRecorded}
            </div>
            <div className="text-[9px] font-[600] text-[#9E9890]">
              {isEs
                ? `${intelligenceSummary.quotesReceived} quotes · ${intelligenceSummary.pricesRecorded} precios`
                : `${intelligenceSummary.quotesReceived} quotes · ${intelligenceSummary.pricesRecorded} prices`}
            </div>
          </div>
        </div>
      )}

      {intelligenceSummary && intelligenceSummary.recentSignals.length > 0 && (
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-[13px] font-[800] text-[#1C1A17]">
                {isEs ? 'Señales vivas del proyecto' : 'Live project signals'}
              </h3>
              <p className="text-[10px] font-[600] text-[#9E9890] mt-0.5">
                {isEs
                  ? 'Actividad real reciente capturada por la capa de inteligencia.'
                  : 'Recent real activity captured by the intelligence layer.'}
              </p>
            </div>
            <div className="text-[10px] font-[800] text-[#06539B] bg-[#F8FBFF] border border-[#DBEAFE] rounded-full px-2.5 py-1">
              {intelligenceSummary.recentSignals.length} {isEs ? 'señales' : 'signals'}
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
            {intelligenceSummary.recentSignals.map(signal => (
              <div key={`${signal.eventName}-${signal.createdAt}`} className="rounded-lg border border-[#F2F0EB] bg-[#FAFAF8] px-3 py-2.5">
                <div className="text-[11px] font-[800] text-[#1C1A17]">
                  {formatSignalLabel(signal.eventName, isEs)}
                </div>
                <div className="text-[9px] font-[600] text-[#9E9890] mt-0.5">
                  {new Date(signal.createdAt).toLocaleString(isEs ? 'es-MX' : 'en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-[#22C55E]/5 border border-[#22C55E]/20 rounded-xl p-4">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#22C55E]">
            {isEs ? 'Ahorro potencial' : 'Potential savings'}
          </div>
          <div className="text-[24px] font-[900] tracking-[-1px] text-[#22C55E]">${(totalSavingsOpportunity / 1000).toFixed(1)}K</div>
          <div className="text-[9px] font-[600] text-[#9E9890]">{isEs ? 'señal actual del proyecto' : 'current project signal'}</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#06539B]">
            {isEs ? 'Confiabilidad prom.' : 'Avg reliability'}
          </div>
          <div className="text-[24px] font-[900] tracking-[-1px] text-blue-700">{avgReliability.toFixed(1)}<span className="text-[14px]">/5</span></div>
          <div className="text-[9px] font-[600] text-[#9E9890]">{isEs ? 'en este proyecto' : 'in this project'}</div>
        </div>
        <div className={`${overBudgetCategories > 2 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'} border rounded-xl p-4`}>
          <div className={`text-[9px] font-[800] uppercase tracking-[0.5px] ${overBudgetCategories > 2 ? 'text-red-600' : 'text-amber-600'}`}>
            {isEs ? 'Rubros excedidos' : 'Over budget items'}
          </div>
          <div className={`text-[24px] font-[900] tracking-[-1px] ${overBudgetCategories > 2 ? 'text-red-700' : 'text-amber-700'}`}>{overBudgetCategories}</div>
          <div className="text-[9px] font-[600] text-[#9E9890]">{isEs ? 'categorías arriba de 5%' : 'categories above 5%'}</div>
        </div>
        <div className={`${bottleneckPhases > 1 ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'} border rounded-xl p-4`}>
          <div className={`text-[9px] font-[800] uppercase tracking-[0.5px] ${bottleneckPhases > 1 ? 'text-orange-600' : 'text-green-600'}`}>
            {isEs ? 'Cuellos de botella' : 'Bottlenecks'}
          </div>
          <div className={`text-[24px] font-[900] tracking-[-1px] ${bottleneckPhases > 1 ? 'text-orange-700' : 'text-green-700'}`}>{bottleneckPhases}</div>
          <div className="text-[9px] font-[600] text-[#9E9890]">{isEs ? 'fases bloqueadas/críticas' : 'blocked/critical phases'}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border whitespace-nowrap transition-all duration-200 flex-shrink-0 ${
              activeTab === tab.key
                ? 'bg-[#06539B] text-white border-[#06539B]'
                : 'bg-white border-[#ECEAE4] text-[#9E9890] hover:border-[#06539B]'
            }`}
          >
            <tab.icon size={14} />
            <span className="text-[11px] font-[800]">{tab.label}</span>
            <span className={`text-[9px] font-[800] px-1.5 py-0.5 rounded-full ${
              activeTab === tab.key ? 'bg-white/20' : 'bg-[#F2F0EB]'
            }`}>{tab.count}</span>
          </button>
        ))}
      </div>

      {/* PR-NEW: honest empty state for real projects with no data yet.
          When the project is a real one AND none of the real-insight
          buckets carry data, render the bilingual empty-state banner
          and skip the tab content entirely (so no demo suggestions /
          suppliers / budget / timeline leak in via the tab renderers
          below). Demo projects fall through unchanged. */}
      {!isDemo && !hasAnyRealInsightData ? (
        <div
          className="text-center py-12 bg-[#FAFAF8] border border-dashed border-[#ECEAE4] rounded-xl"
          data-testid="insights-honest-empty-state"
        >
          <Brain size={36} className="mx-auto text-[#ECEAE4] mb-3" />
          <p className="text-[12px] font-[700] text-[#1C1A17] max-w-md mx-auto leading-snug">
            {isEs
              ? 'Conecta tu proyecto: agrega cotizaciones, contratos y recibos para ver insights reales.'
              : 'Connect your project: add quotes, contracts, and receipts to see real insights.'}
          </p>
        </div>
      ) : (
        <>
          {/* Content — demo projects keep the demo data, real projects
              with at least some data render whatever is real. The
              isDemo gate prevents demo data from filling in for empty
              real buckets. */}
          {activeTab === 'suggestions' && <SuggestionsTab suggestions={realSuggestions || (isDemo ? DEMO_SUGGESTIONS : [])} isEs={isEs} />}
          {activeTab === 'suppliers' && <SuppliersTab suppliers={realSuppliers || (isDemo ? DEMO_SUPPLIERS : [])} isEs={isEs} />}
          {activeTab === 'materials' && <MaterialsTab materials={isDemo ? DEMO_MATERIALS : []} realMaterials={realMaterials} isEs={isEs} />}
          {activeTab === 'budget' && <BudgetTab budgets={isDemo ? DEMO_BUDGET : []} realBudget={realBudget} isEs={isEs} />}
          {activeTab === 'timeline' && <TimelineTab timeline={isDemo ? DEMO_TIMELINE : []} realTimeline={realTimeline} isEs={isEs} />}
        </>
      )}
    </div>
  )
}

// ── SUGGESTIONS TAB ───────────────────────────────

function SuggestionsTab({ suggestions, isEs }: { suggestions: AISuggestion[]; isEs: boolean }) {
  const typeIcons = { savings: DollarSign, efficiency: Zap, risk: AlertTriangle, quality: Star }
  const typeColors = {
    savings: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', icon: 'text-green-500' },
    efficiency: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: 'text-blue-500' },
    risk: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: 'text-red-500' },
    quality: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', icon: 'text-purple-500' },
  }
  const priorityColors = { high: 'bg-red-100 text-red-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-gray-100 text-gray-600' }

  return (
    <div className="space-y-3">
      {suggestions.map(s => {
        const Icon = typeIcons[s.type]
        const colors = typeColors[s.type]
        return (
          <div key={s.id} className={`${colors.bg} border ${colors.border} rounded-xl p-5`}>
            <div className="flex items-start gap-3">
              <div className={`w-[36px] h-[36px] rounded-xl ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                <Icon size={18} className={colors.icon} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className={`text-[13px] font-[800] ${colors.text}`}>{isEs ? s.titleEs : s.title}</h3>
                  <span className={`text-[8px] font-[800] px-2 py-0.5 rounded-full uppercase ${priorityColors[s.priority]}`}>
                    {isEs ? ({ high: 'ALTA', medium: 'MEDIA', low: 'BAJA' }[s.priority]) : s.priority}
                  </span>
                  <span className="text-[9px] font-[700] text-[#9E9890] bg-white/50 px-2 py-0.5 rounded-full">
                    {s.confidence}% {isEs ? 'confianza' : 'confidence'}
                  </span>
                </div>
                <p className="text-[11px] font-[600] text-[#1C1A17]/70 mt-1.5 leading-[1.6]">
                  {isEs ? s.descriptionEs : s.description}
                </p>
                <div className="flex items-center gap-4 mt-3">
                  {s.potentialSavings && (
                    <span className="text-[12px] font-[900] text-[#22C55E]">
                      ${s.potentialSavings.toLocaleString()} {isEs ? 'ahorro potencial' : 'potential savings'}
                    </span>
                  )}
                  <span className="text-[9px] font-[600] text-[#9E9890]">
                    {isEs ? 'Fuente' : 'Source'}: {isEs ? (s.sourceEs || s.source) : s.source}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── SUPPLIERS TAB ─────────────────────────────────

function SuppliersTab({ suppliers, isEs }: { suppliers: SupplierInsight[]; isEs: boolean }) {
  return (
    <div className="space-y-3">
      {suppliers.map((s, i) => (
        <div key={i} className="bg-white border border-[#ECEAE4] rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-[13px] font-[800] text-[#1C1A17]">{s.name}</h3>
              <span className="text-[12px] font-[700] text-[#9E9890]">{s.category} · {s.timesUsed} {isEs ? 'proyectos' : 'projects'}</span>
            </div>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map(star => (
                <Star key={star} size={12} className={star <= Math.round(s.reliabilityScore) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'} />
              ))}
              <span className="text-[10px] font-[800] text-[#1C1A17] ml-1">{s.reliabilityScore}</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="bg-[#FAFAF8] rounded-lg p-2.5">
              <div className="text-[9px] font-[700] text-[#9E9890] uppercase">{isEs ? 'Tu costo prom.' : 'Your avg cost'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">${s.avgCost.toLocaleString()}</div>
            </div>
            <div className="bg-[#FAFAF8] rounded-lg p-2.5">
              <div className="text-[9px] font-[700] text-[#9E9890] uppercase">{isEs ? 'Promedio mercado' : 'Market avg'}</div>
              <div className="text-[14px] font-[900] text-[#1C1A17]">${s.marketAvg.toLocaleString()}</div>
            </div>
            <div className={`rounded-lg p-2.5 ${s.savingsOrLoss >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className="text-[9px] font-[700] text-[#9E9890] uppercase">
                {s.savingsOrLoss >= 0 ? (isEs ? 'Ahorrando' : 'Saving') : (isEs ? 'Pagando de mas' : 'Overpaying')}
              </div>
              <div className={`text-[14px] font-[900] flex items-center gap-1 ${s.savingsOrLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {s.savingsOrLoss >= 0 ? <ArrowDownRight size={14} /> : <ArrowUpRight size={14} />}
                ${Math.abs(s.savingsOrLoss).toLocaleString()}
              </div>
            </div>
          </div>
          {s.suggestion && (
            <div className="flex items-start gap-2 bg-[#06539B]/5 border border-[#06539B]/10 rounded-lg p-2.5">
              <Lightbulb size={14} className="text-[#06539B] flex-shrink-0 mt-0.5" />
              <p className="text-[10px] font-[600] text-[#06539B]/80 leading-[1.5]">
                {isEs ? s.suggestionEs : s.suggestion}
              </p>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── MATERIALS TAB ─────────────────────────────────

function MaterialsTab({
  materials,
  realMaterials,
  isEs
}: {
  materials: MaterialInsight[]
  realMaterials?: MaterialInsight[] | null
  isEs: boolean
}) {
  const rows = realMaterials && realMaterials.length > 0 ? realMaterials : materials

  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
      {realMaterials && realMaterials.length > 0 && (
        <div className="px-4 py-3 border-b border-[#ECEAE4] bg-[#F8FBFF]">
          <p className="text-[10px] font-[700] text-[#4B5563]">
            {isEs
              ? 'Basado en señales reales de cotizaciones/precios disponibles para este proyecto.'
              : 'Based on real quote/price signals available for this project.'}
          </p>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-[#FAFAF8] border-b border-[#ECEAE4]">
              <th className="text-left text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.5px] px-4 py-3">{isEs ? 'Material' : 'Material'}</th>
              <th className="text-right text-[10px] font-[800] text-[#9E9890] uppercase px-4 py-3">{isEs ? 'Prom/Proyecto' : 'Avg/Project'}</th>
              <th className="text-right text-[10px] font-[800] text-[#9E9890] uppercase px-4 py-3">{isEs ? 'Tu precio' : 'Your price'}</th>
              <th className="text-right text-[10px] font-[800] text-[#9E9890] uppercase px-4 py-3">{isEs ? 'Mercado' : 'Market'}</th>
              <th className="text-right text-[10px] font-[800] text-[#9E9890] uppercase px-4 py-3">{isEs ? 'Tendencia' : 'Trend'}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F2F0EB]">
            {rows.map((m, i) => (
              <tr key={i} className="hover:bg-[#FAFAF8] transition-colors">
                <td className="px-4 py-3">
                  <div className="text-[12px] font-[700] text-[#1C1A17]">{isEs ? m.materialEs : m.material}</div>
                  <div className="text-[9px] font-[600] text-[#9E9890]">
                    {m.unit}
                    {typeof m.avgLeadTimeDays === 'number' ? ` · ${m.avgLeadTimeDays}${isEs ? ' días lead' : 'd lead'}` : ''}
                  </div>
                </td>
                <td className="text-right px-4 py-3 text-[11px] font-[700] text-[#9E9890]">
                  {m.avgQtyPerProject} {realMaterials ? (isEs ? 'quotes' : 'quotes') : m.unit}
                </td>
                <td className="text-right px-4 py-3 text-[12px] font-[800] text-[#1C1A17]">${m.avgCostPerUnit.toFixed(2)}/{m.unit}</td>
                <td className="text-right px-4 py-3 text-[11px] font-[700] text-[#9E9890]">
                  ${(m.bestObservedPrice ?? m.marketAvg).toFixed(2)}
                </td>
                <td className="text-right px-4 py-3">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-[800] px-2 py-0.5 rounded-full ${
                    m.priceChange > 3 ? 'bg-red-50 text-red-600' :
                    m.priceChange < -2 ? 'bg-green-50 text-green-600' :
                    'bg-gray-50 text-gray-500'
                  }`}>
                    {m.priceChange > 0 ? <ArrowUpRight size={10} /> : m.priceChange < 0 ? <ArrowDownRight size={10} /> : null}
                    {m.priceChange > 0 ? '+' : ''}{m.priceChange.toFixed(1)}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── BUDGET TAB ────────────────────────────────────

function BudgetTab({
  budgets,
  realBudget,
  isEs
}: {
  budgets: BudgetInsight[]
  realBudget?: {
    categories: RealBudgetCategory[]
    totals: {
      budgeted: number
      actual: number
      variance: number
    }
  } | null
  isEs: boolean
}) {
  if (realBudget && realBudget.categories.length > 0) {
    const isOver = realBudget.totals.variance > 0

    return (
      <div className="space-y-4">
        <div className="bg-[#06539B] text-white rounded-2xl p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-white/50">{isEs ? 'Presupuesto' : 'Budgeted'}</div>
            <div className="text-[28px] font-[900] tracking-[-1px]">${Math.round(realBudget.totals.budgeted).toLocaleString()}</div>
          </div>
          <div>
            <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-white/50">{isEs ? 'Gastado' : 'Actual spent'}</div>
            <div className="text-[28px] font-[900] tracking-[-1px] text-sky-300">${Math.round(realBudget.totals.actual).toLocaleString()}</div>
          </div>
          <div>
            <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-white/50">{isEs ? 'Variación' : 'Variance'}</div>
            <div className={`text-[28px] font-[900] tracking-[-1px] ${isOver ? 'text-amber-300' : 'text-emerald-300'}`}>
              {realBudget.totals.variance >= 0 ? '+' : ''}{realBudget.totals.variance.toFixed(1)}%
            </div>
          </div>
          <div>
            <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-white/50">{isEs ? 'Categorías' : 'Categories'}</div>
            <div className="text-[28px] font-[900] tracking-[-1px]">{realBudget.categories.length}</div>
          </div>
        </div>

        <div className="space-y-3">
          {realBudget.categories.map((b, i) => {
            const isCategoryOver = b.variance > 0
            const barWidth = Math.min(Math.abs(b.variance), 20) * 5
            return (
              <div key={`${b.category}-${i}`} className="bg-white border border-[#ECEAE4] rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="text-[13px] font-[800] text-[#1C1A17]">{isEs ? b.categoryEs : b.category}</h3>
                    <span className="text-[10px] font-[600] text-[#9E9890]">
                      {b.itemCount} {isEs ? 'partidas' : 'line items'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-[800] px-2 py-0.5 rounded-full ${
                      b.variance < -5 ? 'bg-green-50 text-green-600' :
                      b.variance > 5 ? 'bg-red-50 text-red-600' :
                      'bg-gray-50 text-gray-500'
                    }`}>
                      {b.variance < -5
                        ? (isEs ? '↗ Mejorando' : '↗ Improving')
                        : b.variance > 5
                          ? (isEs ? '↘ Presión' : '↘ Pressure')
                          : (isEs ? '→ Estable' : '→ Stable')}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-4 mb-2">
                  <div className="flex-1">
                    <div className="text-[9px] font-[700] text-[#9E9890]">{isEs ? 'Presupuestado' : 'Budgeted'}</div>
                    <div className="text-[14px] font-[900]">${Math.round(b.budgeted).toLocaleString()}</div>
                  </div>
                  <div className="flex-1">
                    <div className="text-[9px] font-[700] text-[#9E9890]">{isEs ? 'Gastado real' : 'Actual spent'}</div>
                    <div className="text-[14px] font-[900]">${Math.round(b.actual).toLocaleString()}</div>
                  </div>
                  <div className="flex-1 text-right">
                    <div className="text-[9px] font-[700] text-[#9E9890]">{isEs ? 'Variación' : 'Variance'}</div>
                    <div className={`text-[14px] font-[900] ${isCategoryOver ? 'text-red-600' : 'text-green-600'}`}>
                      {b.variance >= 0 ? '+' : ''}{b.variance.toFixed(1)}%
                    </div>
                  </div>
                </div>
                <div className="h-[6px] bg-[#F2F0EB] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${isCategoryOver ? 'bg-red-400' : 'bg-green-400'}`}
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {budgets.map((b, i) => {
        const isOver = b.variance > 0
        const barWidth = Math.min(Math.abs(b.variance), 20) * 5 // max 100%
        return (
          <div key={i} className="bg-white border border-[#ECEAE4] rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-[13px] font-[800] text-[#1C1A17]">{isEs ? b.categoryEs : b.category}</h3>
                <span className="text-[10px] font-[600] text-[#9E9890]">{b.projects} {isEs ? 'proyectos analizados' : 'projects analyzed'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-[800] px-2 py-0.5 rounded-full ${
                  b.trend === 'improving' ? 'bg-green-50 text-green-600' :
                  b.trend === 'worsening' ? 'bg-red-50 text-red-600' :
                  'bg-gray-50 text-gray-500'
                }`}>
                  {b.trend === 'improving' ? (isEs ? '↗ Mejorando' : '↗ Improving') : b.trend === 'worsening' ? (isEs ? '↘ Empeorando' : '↘ Worsening') : (isEs ? '→ Estable' : '→ Stable')}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4 mb-2">
              <div className="flex-1">
                <div className="text-[9px] font-[700] text-[#9E9890]">{isEs ? 'Presupuestado' : 'Budgeted'}</div>
                <div className="text-[14px] font-[900]">${b.avgBudgeted.toLocaleString()}</div>
              </div>
              <div className="flex-1">
                <div className="text-[9px] font-[700] text-[#9E9890]">{isEs ? 'Gastado real' : 'Actual spent'}</div>
                <div className="text-[14px] font-[900]">${b.avgActual.toLocaleString()}</div>
              </div>
              <div className="flex-1 text-right">
                <div className="text-[9px] font-[700] text-[#9E9890]">{isEs ? 'Variacion' : 'Variance'}</div>
                <div className={`text-[14px] font-[900] ${isOver ? 'text-red-600' : 'text-green-600'}`}>
                  {isOver ? '+' : ''}{b.variance.toFixed(1)}%
                </div>
              </div>
            </div>
            {/* Variance bar */}
            <div className="h-[6px] bg-[#F2F0EB] rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${isOver ? 'bg-red-400' : 'bg-green-400'}`}
                style={{ width: `${barWidth}%` }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── TIMELINE TAB ──────────────────────────────────

function TimelineTab({
  timeline,
  realTimeline,
  isEs
}: {
  timeline: TimelineInsight[]
  realTimeline?: {
    phases: RealTimelinePhase[]
    totals: {
      totalTasks: number
      completedTasks: number
      blockedTasks: number
      inProgressTasks: number
      pendingTasks: number
    }
  } | null
  isEs: boolean
}) {
  if (realTimeline && realTimeline.phases.length > 0) {
    const { totals, phases } = realTimeline

    return (
      <div className="space-y-4">
        <div className="bg-[#06539B] text-white rounded-2xl p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-white/50">{isEs ? 'Tareas totales' : 'Total tasks'}</div>
            <div className="text-[28px] font-[900] tracking-[-1px]">{totals.totalTasks}</div>
          </div>
          <div>
            <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-white/50">{isEs ? 'Completadas' : 'Completed'}</div>
            <div className="text-[28px] font-[900] tracking-[-1px] text-emerald-300">{totals.completedTasks}</div>
          </div>
          <div>
            <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-white/50">{isEs ? 'En progreso' : 'In progress'}</div>
            <div className="text-[28px] font-[900] tracking-[-1px] text-sky-300">{totals.inProgressTasks}</div>
          </div>
          <div>
            <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-white/50">{isEs ? 'Bloqueadas' : 'Blocked'}</div>
            <div className="text-[28px] font-[900] tracking-[-1px] text-amber-300">{totals.blockedTasks}</div>
          </div>
        </div>

        <div className="space-y-2">
          {phases.map((phase, i) => (
            <div
              key={`${phase.phase}-${i}`}
              className={`bg-white border rounded-xl p-4 ${phase.blockedTasks > 0 ? 'border-amber-200 bg-amber-50/30' : 'border-[#ECEAE4]'}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  {phase.blockedTasks > 0 && <AlertTriangle size={14} className="text-amber-500" />}
                  <div>
                    <h3 className="text-[12px] font-[800] text-[#1C1A17]">{isEs ? phase.phaseEs : phase.phase}</h3>
                    <span className="text-[10px] font-[600] text-[#9E9890]">
                      {phase.totalTasks} {isEs ? 'tareas' : 'tasks'} · {phase.completedTasks} {isEs ? 'completadas' : 'completed'} · {phase.upcomingTasks} {isEs ? 'pendientes' : 'upcoming'}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[12px] font-[900] text-[#1C1A17]">
                    {new Date(phase.earliestStart).toLocaleDateString(isEs ? 'es-MX' : 'en-US', { month: 'short', day: 'numeric' })}
                    {' → '}
                    {new Date(phase.latestEnd).toLocaleDateString(isEs ? 'es-MX' : 'en-US', { month: 'short', day: 'numeric' })}
                  </div>
                  <div className={`text-[8px] font-[800] uppercase mt-0.5 ${phase.criticalTasks > 0 ? 'text-red-500' : 'text-[#9E9890]'}`}>
                    {phase.criticalTasks > 0
                      ? (isEs ? `${phase.criticalTasks} críticas` : `${phase.criticalTasks} critical`)
                      : (isEs ? 'sin críticas' : 'no critical')}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const totalPlanned = timeline.reduce((sum, t) => sum + t.avgPlannedDays, 0)
  const totalActual = timeline.reduce((sum, t) => sum + t.avgActualDays, 0)

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="bg-[#06539B] text-white rounded-2xl p-5 flex items-center justify-between">
        <div>
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-white/50">{isEs ? 'Promedio planeado' : 'Avg planned'}</div>
          <div className="text-[28px] font-[900] tracking-[-1px]">{totalPlanned} {isEs ? 'dias' : 'days'}</div>
        </div>
        <div className="text-[24px] font-[900] text-white/30">→</div>
        <div>
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-white/50">{isEs ? 'Promedio real' : 'Avg actual'}</div>
          <div className="text-[28px] font-[900] tracking-[-1px] text-red-400">{totalActual} {isEs ? 'dias' : 'days'}</div>
        </div>
        <div className="text-right">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-white/50">{isEs ? 'Retraso total' : 'Total delay'}</div>
          <div className="text-[28px] font-[900] tracking-[-1px] text-amber-400">+{totalActual - totalPlanned} {isEs ? 'dias' : 'days'}</div>
        </div>
      </div>

      {/* Phase breakdown */}
      <div className="space-y-2">
        {timeline.map((phase, i) => {
          const delayPct = phase.avgPlannedDays > 0 ? ((phase.avgActualDays - phase.avgPlannedDays) / phase.avgPlannedDays) * 100 : 0
          return (
            <div key={i} className={`bg-white border rounded-xl p-4 ${phase.bottleneck ? 'border-red-200 bg-red-50/30' : 'border-[#ECEAE4]'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {phase.bottleneck && <AlertTriangle size={14} className="text-red-500" />}
                  <div>
                    <h3 className="text-[12px] font-[800] text-[#1C1A17]">{isEs ? phase.phaseEs : phase.phase}</h3>
                    <span className="text-[10px] font-[600] text-[#9E9890]">
                      {phase.avgPlannedDays}d {isEs ? 'planeado' : 'planned'} → {phase.avgActualDays}d {isEs ? 'real' : 'actual'}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`text-[12px] font-[900] ${
                    phase.variance > 3 ? 'text-red-600' : phase.variance > 0 ? 'text-amber-600' : 'text-green-600'
                  }`}>
                    {phase.variance > 0 ? '+' : ''}{phase.variance} {isEs ? 'dias' : 'days'}
                  </span>
                  {phase.bottleneck && (
                    <div className="text-[8px] font-[800] text-red-500 uppercase mt-0.5">
                      {isEs ? 'Cuello de botella' : 'Bottleneck'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
